import { CollectionViewer, isDataSource, ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    DoCheck,
    EventEmitter,
    Host,
    Inject,
    Input,
    isDevMode,
    IterableDiffer,
    IterableDiffers,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    TemplateRef,
    TrackByFunction,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
    DOCUMENT
} from '@angular/core';
import { Coerce, KalSelectionModel, KalThemeDirective } from '@kalidea/kaligraphi';
import { BehaviorSubject, isObservable, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { delay, startWith, takeUntil, tap } from 'rxjs/operators';
import { KalFixedTableDirective } from 'src/app/_shared/modules/kal-table/kal-fixed-table.directive';
import {
    KalColumnResizeDefinition,
    KalTableColumnResizeService
} from 'src/app/_shared/modules/kal-table/services/kal-table-column-resize.service';

import { KalTableDatasource } from './classes/kal-table-datasource';
import { KalTableHeader } from './classes/kal-table-header';
import { KalTemplateOutletCallback } from './directives/kal-template-outlet.directive';
import {
    KalBaseCellDirective,
    KalBaseOutletDirective,
    KalBaseRowDirective,
    KalBodyCellDirective,
    KalBodyRowDirective,
    KalBodyRowOutletDirective,
    KalCellsOutletDirective,
    KalFooterCellDirective,
    KalFooterRowDirective,
    KalFooterRowOutletDirective,
    KalHeaderCellDirective,
    KalHeaderRowDirective,
    KalHeaderRowOutletDirective
} from './kal-table-definitions';
import {
    KalTableGenericDatasource
} from 'src/app/_shared/modules/kal-table/classes/kal-table-generic-datasource';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import {
    ColumnFilter,
    ColumnFilterType
} from 'src/app/_shared/modules/kal-table/kal-table-header/kal-table-header.component';

// first letter in upper case to avoid string manipulation
interface KalTableInformation<T = any> {
    Header: T;
    Body: T;
    Footer: T;
}

export type KalTableDataSourceInput<T> =
    KalTableDatasource<T> | Observable<ReadonlyArray<T> | T[]> | ReadonlyArray<T> | T[];

export enum KalSelectionMode {
    None = 'none',
    Single = 'single',
    Multiple = 'multiple'
}

export interface KalRowContext<T> {
    /** Data for the row that this cell is located within. */
    $implicit?: T;

    /** code for column */
    code?: string;

    /** Index of the data object in the provided data array. */
    index?: number;

    /** Length of the number of total rows. */
    count?: number;

    /** True if this cell is contained in the first row. */
    first?: boolean;

    /** True if this cell is contained in the last row. */
    last?: boolean;

    /** True if this cell is contained in a row with an even-numbered index. */
    even?: boolean;

    /** True if this cell is contained in a row with an odd-numbered index. */
    odd?: boolean;
}

/*

 How to use
 <kal-table
 #table
 [columns]="columns"
 [displayedColumns]="displayedColumns"
 [dataSource]="datasource"
 [selection]="selection"

 // option pour ajouter une colonne d'action en tant que dernière colonne
 [actionable]="actionable"

 // pour la virtualisation
 [virtual]="true"
 [virtualItemSize]="10"
 virtualViewPortHeight="400px"

 // pour les évènements de sortie
 (cellClick)="cellClick($event)"
 (rowClick)="rowClick($event)">

     <tr *kalBodyRowDef kal-body-row></tr> <!-- pas obligatoire, permet de surcharger l'affichage des lignes du tbody -->
     <tr *kalHeaderRowDef kal-header-row></tr> <!-- requis si besoin d'afficher le footer -->
     <tr *kalFooterRowDef kal-footer-row></tr> <!-- requis si besoin d'afficher le footer -->

     <!--
        définition des types de cellule d'une colonne,
        ( pas toutes obligatoires : les cellules non spécifiées seront affichée avec la vue par défaut )
        Avec cette méthode, le nom de la colonne est récupéré depuis le KalColumn,
        pas besoin de spécifier pour chaque type de cellule ( head, body, row )
       -->
     <ng-container [kalColumn]="'firstname'">
         <th *kalHeaderCell>firstname</th>
         <td *kalBodyCell="let row">{{ row.firstname }}</td>
         <td *kalFooterCell>Firstname footer</td>
     </ng-container>

    <!--
     définition d'un type de cellule ( head, body, foot ) pour surcharger la vue par défaut.
     Dans ce cas, le nom de la colonne est requis dans l'input de la directive
    -->
     <th *kalHeaderCell="'lastname'">lastname</th>
     <td *kalBodyCell="'lastname' let row">{{ row.lastname }}</td>
     <td *kalFooterCell="'lastname'">Hey 2</td>

    <!--
        possibilité de récupérer les informations de la ligne courante dans le contexte: KalRowContext
        index, last, first, count, even, odd
     -->
     <tr *kalBodyRowDef="let row let index=index let even=even" [class.even]="even" kal-body-row></tr>

     <!--
       /!\ Dans le cas de la cellule: il s'agit du contexte de la ligne et non pas de la cellule
       donc index, last, first, count, even, odd seront en rapport avec la ligne et non pas en rapport avec la cellule
       par contre, le code de la colonne est disponible ici alors qu'il n'est pas dispo dans le contexte de la ligne directement
       -->
     <td *kalBodyCell="'city' let row let index=index let code=code">{{ row.city }}</td>

    <!-- utilisation de la colonne d'action -->
    <ng-container [kalColumn]="table.actionColumnName">
        <td *kalBodyCell="let row">
            <kal-button (click)="someAction(row)"><kal-icon>icon_name</kal-icon></kal-button>
        </td>
    </ng-container>

 </kal-table>
 */

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'kal-table',
    templateUrl: './kal-table.component.html',
    styleUrls: ['./kal-table.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [KalTableColumnResizeService],
    standalone: false
})
export class KalTableComponent<T>
    implements CollectionViewer, OnInit, OnDestroy, DoCheck, AfterViewChecked {

    readonly selectColumnName = 'kal-select';
    readonly actionColumnName = 'kal-action';
    readonly defaultColumnName = 'kal-default';
    readonly emptyRowClassName = 'kal-row--empty';

    dataStream: Observable<T[] | ReadonlyArray<T>> | undefined;

    readonly viewChange: BehaviorSubject<{ start: number, end: number }> =
        new BehaviorSubject<{ start: number, end: number }>({start: 0, end: Number.MAX_VALUE});

    @Input() columns: KalTableHeader<T>[];
    @Input() @Coerce('boolean') virtual = false; // todo problème lorsque l'on change le boolean : "virtual => !virtual => virtual"
    @Input() @Coerce('boolean') actionable: boolean;
    @Input() @Coerce('boolean') disableSelectAllCheckbox = false;
    @Input() @Coerce('number') virtualItemSize: number;
    @Input() virtualViewPortHeight: string;
    @Input() selection: KalSelectionModel<T>; // should be provided by input
    @Input() placeholder = 'Aucune ligne'; // text displayed when no rows exists in table
    @Input() columnFilters: ColumnFilter[];
    @Output() readonly rowClick = new EventEmitter<T>();
    @Output() readonly cellClick = new EventEmitter<{ resource: T, column: string }>();
    @Output() readonly tableRender = new EventEmitter<void>();
    // customs by user
    @ContentChildren(KalHeaderCellDirective, {descendants: true}) customHeaderCellsTemplateList: QueryList<KalHeaderCellDirective>;

    /////////////////// Definitions of cells
    @ContentChildren(KalFooterCellDirective, {descendants: true}) customFooterCellsTemplateList: QueryList<KalFooterCellDirective>;
    @ContentChildren(KalBodyCellDirective, {descendants: true}) customBodyCellsTemplateList: QueryList<KalBodyCellDirective>;
    // defaults by kal-table-component
    @ViewChildren(KalHeaderCellDirective) defaultHeaderCellTemplate: QueryList<KalHeaderCellDirective>;
    @ViewChildren(KalFooterCellDirective) defaultFooterCellTemplate: QueryList<KalFooterCellDirective>;
    @ViewChildren(KalBodyCellDirective) defaultBodyCellTemplate: QueryList<KalBodyCellDirective>;
    // customs by user
    @ContentChild(KalHeaderRowDirective) customHeaderRowTemplate: KalHeaderRowDirective;

    /////////////////// Definitions of rows
    @ContentChild(KalFooterRowDirective) customFooterRowTemplate: KalFooterRowDirective;
    @ContentChild(KalBodyRowDirective) customBodyRowTemplate: KalBodyRowDirective;
    // defaults by shared-table-component
    @ViewChild(KalBodyRowDirective, {static: true}) defaultBodyRowTemplate: KalBodyRowDirective;
    // Definitions of outlet
    @ViewChild(KalHeaderRowOutletDirective) headerRowOutlet: KalHeaderRowOutletDirective;
    @ViewChild(KalFooterRowOutletDirective) footerRowOutlet: KalFooterRowOutletDirective;
    @ViewChild(KalBodyRowOutletDirective) bodyRowOutlet: KalBodyRowOutletDirective;
    @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;
    @ViewChild(KalFixedTableDirective) viewportFixer: KalFixedTableDirective<T>;
    /** Latest data provided by the data source. */
    private _data: T[] | ReadonlyArray<T>;
    private _displayedColumns: string[] = [];
    private _cellDefs: KalTableInformation<{ [key: string]: TemplateRef<any> }>;
    private _dataSource: KalTableDataSourceInput<T>;
    private _selectionMode: KalSelectionMode = KalSelectionMode.Single;
    /** Subscription that listens for the data provided by the data source. */
    private renderChangeSubscription: Subscription = null;
    private viewportChangeSubscription: Subscription = null;
    /** Subject that emits when the component has been destroyed. */
    private _onDestroy = new Subject<void>();
    /** Differ used to find the changes in the data provided by the data source. */
    private dataDiffer: IterableDiffer<T>; // TODO à utiliser mieux pour optimiser les re-render
    /* track changes on string[] displayedColumn */
    private columnDiffer: IterableDiffer<string>;
    private _trackByFn: TrackByFunction<T>;
    private _hasRows = false;
    private _isFullScreenContext = false;

    constructor(private readonly cdr: ChangeDetectorRef,
                protected readonly differs: IterableDiffers,
                private readonly columnResizeService: KalTableColumnResizeService,
                @Inject(DOCUMENT) private readonly document: Document,
                @Optional() @Host() private readonly themeDirective: KalThemeDirective) {
    }

    /**
     * number of elements
     * SI virtuel, il faut utiliser la variable hasRows
     * SINON, il faut vérifier le nombre d'élément dans les _data sauvegardées à chaque émission du datasource
     */
    get hasRows(): boolean {
        return this.virtual ? this._hasRows : (this._data?.length ?? 0) > 0;
    }

    /**
     * on veut redessiner le tableau si on change le fait qu'il y ai des lignes ou pas
     * ( ca permet d'afficher la ligne ".kal-row--empty" dans le tableau )
     */
    set hasRows(hasRows: boolean) {
        this._hasRows = hasRows;
        this.cdr.markForCheck();
    }

    /**
     * @deprecated
     * on le laisse pour l'instant le temps de corriger tous les tableaux qui l'utilisent encore
     */
    @Input()
    get selectionMode(): KalSelectionMode {
        return this._selectionMode;
    }

    /**
     * @deprecated
     */
    set selectionMode(mode: KalSelectionMode) {
        this._selectionMode = mode;
        if (this.selection) {
            this.selection.multiple = mode === KalSelectionMode.Multiple;
        }
    }

    /**
     * Tracking function that will be used to check the differences in data changes. Used similarly
     * to `ngFor` `trackBy` function. Optimize row operations by identifying a row based on its data
     * relative to the function to know if a row should be added/removed/moved.
     * Accepts a function that takes two parameters, `index` and `item`.
     */
    @Input()
    get trackBy(): TrackByFunction<T> {
        return this._trackByFn;
    }

    set trackBy(fn: TrackByFunction<T>) {
        if (isDevMode() && fn != null && typeof fn !== 'function' && console && console.warn) {
            console.warn(`trackBy must be a function, but received ${JSON.stringify(fn)}.`);
        }
        this._trackByFn = fn;
    }

    @Input()
    get dataSource(): KalTableDataSourceInput<T> {
        return this._dataSource;
    }

    set dataSource(dataSource: KalTableDataSourceInput<T>) {

        // check if datasource is empty array ( because [] !== [] )
        if (Array.isArray(this._dataSource) && Array.isArray(dataSource)
            && this._dataSource.length === 0 && dataSource.length === 0) {
            return; // if previous was [] and new is [] , we should not update table
        }
        if (this._dataSource !== dataSource) {
            this._switchDataSource(dataSource);
        }
    }

    @Input()
    get displayedColumns(): string[] {
        const first = this.shouldDisplayCheckboxCell ? [this.selectColumnName] : [];
        const last = this.actionable ? [this.actionColumnName] : [];
        return [
            ...first,
            ...this._displayedColumns,
            ...last
        ];
    }

    set displayedColumns(columns: string[]) {
        this._displayedColumns = columns;
    }

    get bodyRowTemplate(): KalBodyRowDirective {
        return this.customBodyRowTemplate || this.defaultBodyRowTemplate;
    }

    get shouldDisplayCheckboxCell(): boolean {
        return this.selection && this.selection.multiple;
    }

    get theme(): string | string[] {
        return this.themeDirective?.rawThemes;
    }

    get isFiltered(): boolean {
        return this.dataSource instanceof KalTableGenericDatasource && this.dataSource.isFiltered;
    }

    get isFullScreenContext(): boolean {
        return this._isFullScreenContext;
    }

    set isFullScreenContext(value: boolean) {
        this._isFullScreenContext = value;
    }

    get resizedColumnList(): KalColumnResizeDefinition[] {
        return this.columnResizeService.resizedColumnList;
    }

    private static getColumnClass(code: string): string {
        return 'kal-column-' + code.replace(/[^a-z0-9_-]/ig, '-');
    }

    private _virtualRowClassFunction: (row: T) => string | string[] = (row: T) => '';

    @Input()
    get virtualRowClassFunction(): (row: T) => string | string[] {
        return this._virtualRowClassFunction;
    }

    set virtualRowClassFunction(virtualRowClassFunction: (row: T) => string | string[]) {
        this._virtualRowClassFunction = virtualRowClassFunction || ((row: T) => '');
    }

    /**
     * permet de récupérer le contenu d'une colonne donnée déjà pré-formaté par l'exporter
     */
    format(row: T, code: keyof T): string | T[keyof T] {
        const exporter = this.columns.find(c => c.code === code)?.exporter;
        return exporter?.(row, code) || row[code];
    }

    rowClicked(row: T): void {
        this.rowClick.emit(row);
        this.select(row);
    }

    cellClicked(param: { resource: T; column: string }): void {
        this.cellClick.emit(param);
    }

    select(row: T, $event?: MouseEvent): void {
        if (this.selection) {
            this.selection.toggle(row);
        }
        this.stopEvent($event);
    }

    isSelected(row: T): boolean {
        return this.selection && this.selection.isSelected(row);
    }

    getColumn(code: string): KalTableHeader {
        return this.columns.find(c => c.code === code);
    }

    render({recalculateColumnWidths}: {recalculateColumnWidths: boolean} = {recalculateColumnWidths: false}): void {
        this.renderHeaderRow();
        this.renderFooterRow();
        // render body only when not virtual

        if (!this.virtual) {
            this.renderBodyRows();
        }
        this.cdr.detectChanges();
        this.tableRender.emit();

        if (recalculateColumnWidths) {
            this.viewportFixer.recalculWidths();
        }
    }

    /**
     * get cell template by code and type
     */
    getCellTemplate(code: string, type: keyof KalTableInformation = 'Body'): TemplateRef<any> {
        if (this._cellDefs && this._cellDefs[type][code]) {
            return this._cellDefs[type][code];
        } else {
            // if no match in cache, try to find on viewchildren
            const list = (this['default' + type + 'CellTemplate'] as QueryList<KalBaseCellDirective>);
            let match = list.find(d => d.name === code);
            // if no match in viewchildren, get default view
            if (!match) {
                // override "defaultColumnName" with provided content child for more flexibility
                match = (this['custom' + type + 'CellsTemplateList'] as QueryList<KalBaseCellDirective>)
                    .find(d => d.name === this.defaultColumnName);

                if (!match) {
                    match = list.find(d => d.name === this.defaultColumnName);
                }
            }
            return match.template;
        }
    }

    getRowContext(param: KalRowContext<T>): KalRowContext<T> {
        const context = {...param};
        if (context.count) {
            context.last = context.index === context.count - 1;
        }
        context.first = context.index === 0;
        context.even = context.index % 2 === 0;
        context.odd = !context.even;
        return context;
    }

    selectAll($event?: MouseEvent): void {
        if (this.selection) {
            const {multiple, all} = this.selection;
            if (multiple) {
                this.selection[all ? 'clear' : 'selectAll'](true);
            }
        }
        this.stopEvent($event);
    }

    isAllSelected(): boolean {
        return this.selection && this.selection.all;
    }

    /**
     * callback appelé juste après la création d'une vue dans un kalTemplateOutlet
     */
    addClassOnCreatedCell(code: string): KalTemplateOutletCallback {
        return node => node[0]?.classList?.add(KalTableComponent.getColumnClass(code));
    }

    updateResizedHeaderList(columnResize: KalColumnResizeDefinition): void {
        this.columnResizeService.updateResizedColumn(columnResize);
    }

    /**
     * Si il y a un évènement fourni, il faut stopper la propagation de l'évènement pour
     * ne pas qu'il se propage à un event handler d'un élément parent, et il faut également
     * empécher le comportement par défaut du click ( qui est dans ce cas là précisement:
     * "click sur le label" => "coche automatiquement le kal-checkbox" )
     */
    private stopEvent($event: Event): void {
        if ($event) {
            $event.stopPropagation();
            $event.preventDefault();
        }
    }

    /**
     * Switch to the provided data source by resetting the data and unsubscribing from the current
     * render change subscription if one exists. If the data source is null, interpret this by
     * clearing the row outlet. Otherwise start listening for new data.
     */
    private _switchDataSource(dataSource: KalTableDataSourceInput<T>): void {
        this._data = [];

        if (isDataSource(this.dataSource)) {
            this.dataSource.disconnect();
        }

        // Stop listening for data from the previous data source.
        if (this.renderChangeSubscription) {
            this.renderChangeSubscription.unsubscribe();
            this.renderChangeSubscription = null;
        }

        // Stop listening from viewport
        if (this.viewportChangeSubscription) {
            this.viewportChangeSubscription.unsubscribe();
            this.viewportChangeSubscription = null;
        }

        if (!dataSource) {
            if (this.dataDiffer) {
                this.dataDiffer.diff([]);
            }

            this.deleteBodyOutlet();
        }

        this._dataSource = dataSource;
    }

    private renderHeaderRow(): void {
        if (this.headerRowOutlet) {
            this.headerRowOutlet.viewContainer.clear();
            this.renderRow(this.headerRowOutlet, this.customHeaderRowTemplate);
        }
    }

    private renderFooterRow(): void {
        if (this.footerRowOutlet) {
            this.footerRowOutlet.viewContainer.clear();
            this.renderRow(this.footerRowOutlet, this.customFooterRowTemplate);
        }
    }

    private renderBodyRows(): void {
        if (this.bodyRowOutlet) {
            this.deleteBodyOutlet();
            for (let i = 0; i < (this._data?.length || 0); i++) {
                const context = this.getRowContext({$implicit: this._data[i], index: i, count: this._data.length});
                this.renderRow(this.bodyRowOutlet, this.bodyRowTemplate, i, context);
            }
        }
    }

    private renderRow(outlet: KalBaseOutletDirective,
                      rowDef: KalBaseRowDirective,
                      index = 0,
                      context: KalRowContext<T> = {}): void {

        if (outlet && rowDef) {
            outlet.viewContainer.createEmbeddedView(rowDef.template, context, index);

            // insert cells in row
            for (const {code, template} of this.getCellsTemplate(rowDef)) {
                const cellContext = this.getRowContext({...context, code});
                if (KalCellsOutletDirective.mostRecentCellOutlet && KalCellsOutletDirective.mostRecentCellOutlet.viewContainer) {
                    const view = KalCellsOutletDirective.mostRecentCellOutlet.viewContainer.createEmbeddedView(template, cellContext);
                    // add css class for th
                    const columnClassName = KalTableComponent.getColumnClass(code);
                    const columnClassList = [columnClassName];
                    const th = (view.rootNodes[0] as HTMLElement);
                    const resizedHeader: KalColumnResizeDefinition | null = this.columnResizeService.resizedColumnList.find(d => d.code === columnClassName) || null;

                    if (resizedHeader) {
                        columnClassList.push('resized');
                        th.style.width = resizedHeader.currentWidth + 'px';
                    }

                    columnClassList.forEach(className => th.classList.add(className));
                }
            }
        }

    }

    private cacheCellsDef(): void {
        if (this._cellDefs) {
            return;
        }
        this._cellDefs = {
            Header: {},
            Body: {},
            Footer: {},
        };
        (['Header', 'Body', 'Footer'] as (keyof KalTableInformation)[]).forEach(type => {
            const cellsList = this['custom' + type + 'CellsTemplateList'];
            if (cellsList) {
                cellsList.forEach(({name, template}) => this._cellDefs[type][name] = template);
            }
        });

    }

    /**
     * get cells template for this row
     */
    private getCellsTemplate(rowDef: KalBaseRowDirective): { code: string; template: TemplateRef<any> }[] {
        let type: keyof KalTableInformation = 'Body';

        if (rowDef instanceof KalHeaderRowDirective) {
            type = 'Header';
        } else if (rowDef instanceof KalBodyRowDirective) {
            type = 'Body';
        } else if (rowDef instanceof KalFooterRowDirective) {
            type = 'Footer';
        }

        return this.displayedColumns.map(code => {
            const template = this.getCellTemplate(code, type);
            return {template, code};
        });
    }

    private observeRenderChanges(): void {
        // If no data source has been set, there is nothing to observe for changes.
        if (!this.dataSource) {
            return;
        }

        // on doit se connecter au datasource uniquement si on est pas dans une virtualisation
        // car dans le cas de la virtualisation, le cdkVirtualFor va s'occuper de connecter tout seul
        // sinon, on a une double connection sur le datasource, une fois ici et une fois dans le html.
        if (!this.virtual) {
            if (isDataSource(this.dataSource)) {
                this.dataStream = this.dataSource.connect(this);
            } else if (isObservable(this.dataSource)) {
                this.dataStream = this.dataSource;
            } else if (Array.isArray(this.dataSource)) {
                this.dataStream = of(this.dataSource);
            }

            this.renderChangeSubscription = this.dataStream
                .pipe(
                    takeUntil(this._onDestroy),
                    tap(data => {
                        this._data = data || [];
                        this.render();
                    })
                )
                .subscribe();
        } else {

            // render header and footer
            this.render();

            // Dans le cas du tableau virtualisé , il faut vérifier si il y a des éléments dans le tableau
            // pour pouvoir afficher ou non la ligne ' aucune ligne '
            const checkIfTableHasRows$ = this.viewport.renderedRangeStream.pipe(
                startWith({start: 1, end: 1} as ListRange),
                delay(10), // on attends que la viewport a regeneré ses lignes
                tap(() => {
                    this.fillColumnFilterLists();

                    // TODO
                    //  TL;DR pas génial, mais pas trouvé mieux
                    //  je ne suis pas sûr que cette modification soit stockée dans le cache du viewport
                    //  donc en revenant sur une ligne déjà chargée auparavant, il se peut que la classe ne soit plus présente,
                    // il faut aussi vérifier le nombre de ligne car le datasource est relié au cdk virtual for et non pas au kal-table
                    // et donc on a pas la liste des éléments du tableau dans la propriété this._data
                    this.hasRows = this.viewport.elementRef.nativeElement.querySelectorAll(`tbody tr:not(.${this.emptyRowClassName})`).length > 0;
                })
            );

            // a chaque modification du datasource, il faut redessiner le tableau
            let datasourceUpdates$: Observable<any> = of(true);
            if (this.dataSource instanceof KalTableDatasource) {
                datasourceUpdates$ = this.dataSource.changes$;
            } else if (isObservable(this.dataSource)) {
                datasourceUpdates$ = this.dataSource;
            }

            this.renderChangeSubscription = merge(
                checkIfTableHasRows$,
                datasourceUpdates$.pipe(tap(() => this.render()))
            ).subscribe();
        }

    }

    private deleteBodyOutlet(): void {
        // quand on est en virtualisé , il n'y a pas de bodyRowOutlet
        if (this.bodyRowOutlet) {
            this.bodyRowOutlet.viewContainer.clear();
        }
    }

    private fillColumnFilterLists(): void {
        if (this.dataSource instanceof KalTableGenericDatasource) {

            // Récupération de la liste possible de choix dans le filtrage de colonne
            const rows = this.dataSource.unfilteredRows;
            if (arrayNotEmpty(rows)) {

                this.columns.filter(
                    column => !!column.columnFilterType
                        && column.columnFilterType === ColumnFilterType.List
                ).forEach(column => {
                    column.dynamicColumnFilterList = [];
                    column.columnFilterList?.every(choice => choice.isDisplayed = false);
                    rows.forEach(row => {
                        const value = row[column.code];
                        if(!!row[column.code] && !column.dynamicColumnFilterList.some(e => e.code === value)) {
                            column.dynamicColumnFilterList.push({code: value, label: value, isDisplayed: true});

                            // Filtrage des valeurs à afficher si c'est une liste personnalisée
                            if (arrayNotEmpty(column.columnFilterList)) {
                                const matchingChoice = column.columnFilterList.find(choice => choice.code === value);
                                if (matchingChoice) {
                                    matchingChoice.isDisplayed = true;
                                }
                            }
                        }
                    });
                });

            } else {

                this.columns.filter(
                    column => !!column.columnFilterType && column.columnFilterType === ColumnFilterType.List
                ).every(column => column.dynamicColumnFilterList = null);

            }

        }
    }

    ngOnDestroy(): void {

        this.deleteBodyOutlet();

        this.headerRowOutlet.viewContainer.clear();
        this.footerRowOutlet.viewContainer.clear();

        this._onDestroy.next();
        this._onDestroy.complete();

        if (isDataSource(this.dataSource)) {
            this.dataSource.disconnect(this);
        }
    }

    ngOnInit(): void {
        // set displayed columns if not set
        if (this._displayedColumns.length === 0) {
            this.displayedColumns = this.columns.map(col => col.code) as string[];
        }

        this.dataDiffer = this.differs.find([]).create((i: number, data: T) => {
            return this.trackBy ? this.trackBy(i, data) : data;
        });

        this.columnDiffer = this.differs.find([]).create((_i: number, column: string) => column);

    }

    ngDoCheck(): void {
        const changes = this.columnDiffer.diff(this.displayedColumns);
        if (changes) {
            this.render();
        }
    }

    ngAfterViewChecked(): void {
        if (this.dataSource && !this.renderChangeSubscription) {
            this.cacheCellsDef();
            this.observeRenderChanges();
        }
    }
}
